import type React from "react";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useId } from "react";
import { twMerge } from "tailwind-merge";

import { ChevronDownIcon } from "@/components/icons";
import { useCollapsibleSections } from "@/features/request/edit/contexts/collapsible-sections.context";
import { useCapturePosthogEvent } from "@/hooks/posthog/useCapturePosthogEvent";
import { useGetRequest } from "@/hooks/useGetRequest";

type CollapsibleBoxProps = {
  children: React.ReactNode;
  className?: string;
  icon: React.ReactNode;
  iconClassName?: string;
  title: string;
  titleChildren?: React.ReactNode;
};

export function CollapsibleBox({
  children,
  className = "",
  icon,
  iconClassName,
  title,
  titleChildren,
}: CollapsibleBoxProps) {
  const { isSectionExpanded, registerSection, setSectionExpanded } =
    useCollapsibleSections();
  const isExpanded = isSectionExpanded(title);
  const contentId = useId();
  const capture = useCapturePosthogEvent();
  const { data: request } = useGetRequest();

  useEffect(() => registerSection(title), [registerSection, title]);

  const toggle = () => {
    setSectionExpanded(title, !isExpanded);
    const requestFields =
      request?.id && request?.creator.id && request?.id && request.projectId;

    if (!requestFields) return;

    if (isExpanded) {
      capture("section_collapsed", {
        field: title,
        projectId: request.projectId,
        requestId: request?.id,
        userId: request?.creator.id,
      });
    } else {
      capture("section_expanded", {
        field: title,
        projectId: request.projectId,
        requestId: request?.id,
        userId: request?.creator.id,
      });
    }
  };

  return (
    <div
      className={twMerge(
        "rounded-xl bg-bg-secondary border border-border-secondary",
        className,
      )}
    >
      <div className="flex w-full items-center gap-xs p-xl justify-between">
        <button
          aria-controls={contentId}
          aria-expanded={isExpanded}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-lg text-left"
          onClick={toggle}
          type="button"
        >
          <div
            className={twMerge(
              "flex shrink-0 items-center justify-center rounded-md border border-border-secondary shadow-xs size-8",
              iconClassName,
            )}
          >
            {icon}
          </div>
          <span className="text-base font-semibold text-text-primary">
            {title}
          </span>
        </button>

        {titleChildren}

        <button
          aria-hidden
          className="shrink-0 cursor-pointer"
          onClick={toggle}
          tabIndex={-1}
          type="button"
        >
          <motion.span
            animate={{ rotate: isExpanded ? 180 : 0 }}
            className="block"
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <ChevronDownIcon className="size-6 text-fg-quinary" />
          </motion.span>
        </button>
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            id={contentId}
            initial={{ height: 0, opacity: 0 }}
            style={{ overflow: "hidden" }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
          >
            <div className="mx-xl h-px bg-border-primary" />
            <div className="p-xl">
              <div className="flex flex-col gap-lg">{children}</div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
